import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Table } from '../../../shared/components/Table';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { formatPrice } from '../../../shared/utils/priceUtils';
import {
  useShippingZones,
  useShippingMode,
  useCreateShippingZone,
  useUpdateShippingZone,
  useDeleteShippingZone,
  useSetShippingMode,
} from '../hooks/useShippingZones';
import { ShippingModeCard } from '../components/ShippingModeCard';
import { ShippingZoneForm, type ConflictInfo } from '../components/ShippingZoneForm';
import { countryName } from '../../../shared/data/countries';
import { ZoneType, RateType, type ShippingZone } from '../api/shippingZonesApi';
import { DEFAULT_ZONE_FORM_VALUES, buildZonePayload, toFormValues, type ZoneFormValues } from '../payload';

const coverage = (zone: ShippingZone): string => {
  if (zone.type === ZoneType.LOCAL_PICKUP) return 'In-store';
  const country = zone.countryCode ? countryName(zone.countryCode) : '—';
  if (zone.stateOrProvinceCodes.length === 0) return `${country} · All states`;
  const codes = zone.stateOrProvinceCodes;
  const shown = codes.slice(0, 2).join(', ');
  const extra = codes.length > 2 ? ` +${codes.length - 2}` : '';
  return `${country} · ${shown}${extra}`;
};

const rateSummary = (zone: ShippingZone): string => {
  const rate = zone.rate;
  if (rate.type === RateType.FREE) return 'Free';
  if (rate.type === RateType.FLAT) return rate.flatAmountMinor != null ? `${formatPrice(rate.flatAmountMinor)} flat` : 'Flat';
  if (rate.type === RateType.WEIGHT_BASED) {
    const count = rate.weightTiers?.length ?? 0;
    return `${count} weight tier${count === 1 ? '' : 's'}`;
  }
  return '—';
};

const etaSummary = (zone: ShippingZone): string => {
  const { deliveryEstimateMinDays: min, deliveryEstimateMaxDays: max } = zone;
  if (min == null && max == null) return '—';
  if (min != null && max != null) return min === max ? `${min} day${min === 1 ? '' : 's'}` : `${min}–${max} days`;
  return `${min ?? max} days`;
};

/**
 * Merchant shipping zones & mode (PE-2172/PE-2173).
 *
 * Scoped by `x-tenant-id` (see shippingZonesApi.ts) rather than an explicit merchantId
 * param, so — unlike branches — there's no need to inject one into mutation payloads.
 */
export function ShippingZonesPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.ORDER_UPDATE_STATUS);

  const zonesQuery = useShippingZones();
  const modeQuery = useShippingMode();
  const createZone = useCreateShippingZone();
  const updateZone = useUpdateShippingZone();
  const deleteZone = useDeleteShippingZone();
  const setMode = useSetShippingMode();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ShippingZone | null>(null);
  const [formValues, setFormValues] = useState<ZoneFormValues>(DEFAULT_ZONE_FORM_VALUES);
  const [conflict, setConflict] = useState<ConflictInfo | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShippingZone | null>(null);

  const openCreate = () => {
    setEditing(null);
    setFormValues(DEFAULT_ZONE_FORM_VALUES);
    setConflict(null);
    setModalOpen(true);
  };
  const openEdit = (zone: ShippingZone) => {
    setEditing(zone);
    setFormValues(toFormValues(zone));
    setConflict(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setConflict(null);
  };

  const isSaving = createZone.isPending || updateZone.isPending;

  const handleSubmit = async () => {
    setConflict(null);
    const payload = buildZonePayload(formValues);
    try {
      if (editing) {
        // `type` is immutable — never send it on update.
        const updatePayload = { ...payload };
        delete (updatePayload as { type?: unknown }).type;
        await updateZone.mutateAsync({ id: editing.id, data: updatePayload });
      } else {
        await createZone.mutateAsync(payload);
      }
      closeModal();
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const message = (err as { message?: string })?.message;
      if (status === 409) {
        setConflict({ status, message });
      }
      // Any other failure was already toasted by ApiFetch.
    }
  };

  const zones = zonesQuery.data ?? [];
  const pickupZone = zones.find((z) => z.type === ZoneType.LOCAL_PICKUP);

  const columns: ColumnDef<ShippingZone>[] = [
    { header: 'Name', accessorKey: 'name', cell: ({ row }) => row.original.name },
    {
      header: 'Type',
      accessorKey: 'type',
      cell: ({ row }) => (row.original.type === ZoneType.LOCAL_PICKUP ? 'Local pickup' : 'Region'),
    },
    { header: 'Coverage', id: 'coverage', cell: ({ row }) => coverage(row.original) },
    { header: 'Rate', id: 'rate', cell: ({ row }) => rateSummary(row.original) },
    { header: 'ETA', id: 'eta', cell: ({ row }) => etaSummary(row.original) },
    {
      header: 'Active',
      accessorKey: 'isActive',
      cell: ({ row }) => (
        <span
          className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${
            row.original.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'
          }`}
        >
          {row.original.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    ...(canManage
      ? [
          {
            header: 'Actions',
            id: 'actions',
            cell: ({ row }: { row: { original: ShippingZone } }) => (
              <div className="space-x-3">
                <button onClick={() => openEdit(row.original)} className="text-xs text-blue-600 hover:underline">
                  Edit
                </button>
                <button
                  onClick={() => setDeleteTarget(row.original)}
                  className="text-xs text-red-500 hover:underline"
                >
                  Delete
                </button>
              </div>
            ),
          } as ColumnDef<ShippingZone>,
        ]
      : []),
  ];

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Shipping zones</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Rates apply per customer state at checkout.
          </p>
        </div>
        {canManage && selectedTenantId && (
          <Button size="sm" className="h-[40px]" onClick={openCreate}>
            Add zone
          </Button>
        )}
      </div>

      {!selectedTenantId ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          Select a merchant to manage their shipping zones.
        </div>
      ) : (
        <>
          <ShippingModeCard
            mode={modeQuery.data}
            isLoading={modeQuery.isLoading}
            onSetMode={(mode) => setMode.mutate(mode)}
            isSaving={setMode.isPending}
            canManage={canManage}
          />

          {zonesQuery.isLoading ? (
            <div className="text-center py-16 text-muted-foreground">Loading shipping zones…</div>
          ) : zonesQuery.error ? (
            <div className="text-center py-16 text-red-500">Failed to load shipping zones. Please try again.</div>
          ) : zones.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
              No shipping zones yet.
              {canManage && ' Click "Add zone" to create one.'}
            </div>
          ) : (
            <div className={pickupZone ? 'bg-card rounded-2xl border border-border overflow-hidden' : ''}>
              <Table data={zones} columns={columns} storageKey="shipping-zones" />
            </div>
          )}
        </>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">{editing ? 'Edit zone' : 'Add zone'}</h2>
            <button onClick={closeModal} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">
              &times;
            </button>
          </div>
          <ShippingZoneForm
            initial={editing}
            values={formValues}
            onChange={setFormValues}
            onSubmit={handleSubmit}
            onCancel={closeModal}
            isSaving={isSaving}
            conflict={conflict}
          />
        </div>
      </Modal>

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="sm">
        <div className="p-8">
          <h3 className="text-lg font-semibold text-foreground mb-2">Delete "{deleteTarget?.name}"?</h3>
          <p className="text-[14px] text-muted-foreground mb-6">
            This zone will no longer be offered at checkout. This cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-[40px]"
              disabled={deleteZone.isPending}
              onClick={async () => {
                if (!deleteTarget) return;
                await deleteZone.mutateAsync(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              {deleteZone.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
